import React from 'react';
import { useHistory } from 'react-router';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { EmailNotFoundError } from 'utils/errors';
import { FormControlComponent } from 'design/formControl';
import { TextComponent } from 'design/text/textComponents';
import { PressControlComponent } from 'design/pressControl';
import { BackControlComponent } from 'design/backControl/backControlComponents';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { validateEmailFormValues } from 'utils/emailFormUtils';
import { EmailFieldset } from 'containers/EmailFieldset';
import { useEmailForm } from 'hooks/useEmailForm';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';

type TProps = {
  onSubmitSucceed(nextEmail: string): void;
};

export const RestorePasswordEmailForm = (props: TProps) => {
  const { onSubmitSucceed } = props;

  const { values, violations, setViolations, setEmailValue } = useEmailForm();

  const intl = useIntlDictionary();

  const history = useHistory();

  const { restorePasswordPage: restorePasswordPageIntl } = intl;

  const { active, injectPromise } = useAsyncBoundary();

  const { sendRestorePasswordEmail } = useServices();

  const submit = React.useCallback(() => {
    setViolations([]);

    const violations = validateEmailFormValues(values);

    if (violations.length > 0) {
      Promise.resolve(violations).then(setViolations);
    } else {
      const promise = sendRestorePasswordEmail({ emailAddress: values.email })
        .then(() => {
          onSubmitSucceed(values.email);
        })
        .catch((error) => {
          if (error instanceof EmailNotFoundError) {
            setViolations([
              {
                name: 'UserNotFound',
              },
            ]);
          } else {
            throw error;
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    setViolations,
    values,
    sendRestorePasswordEmail,
    injectPromise,
    onSubmitSucceed,
  ]);

  return (
    <FormControlComponent
      shouldFocusFirstInvalidInput={violations.length > 0}
      onSubmit={submit}
    >
      <ContentLayoutComponent
        title={
          <TextComponent size="l" family="futura">
            {restorePasswordPageIntl.emailFormTitleText}
          </TextComponent>
        }
        body={
          <>
            <TextComponent size="m">
              {restorePasswordPageIntl.emailFormDescriptionText}
            </TextComponent>
            <MarginComponent top={28} />
            <EmailFieldset
              values={values}
              violations={violations}
              onEmailValueChange={setEmailValue}
            />
            <MarginComponent top={48} />
            <PressControlComponent
              disabled={active}
              theme="primary"
              type="submit"
              hasSpinner={active}
            >
              {restorePasswordPageIntl.actionText}
            </PressControlComponent>
          </>
        }
        backAction={<BackControlComponent onPress={history.goBack} />}
      />
    </FormControlComponent>
  );
};
